import {
  add,
  clamp,
  distance,
  distanceToSegment,
  HOOP,
  lerp,
  normalize,
  scale,
  shotValue,
  smoothstep,
  subtract,
} from "./geometry.js";
import type {
  BallSnapshot,
  DailyCourt,
  DefenderId,
  DefenderSnapshot,
  GameSnapshot,
  OffensivePlayerSpec,
  PassRecord,
  PlayerId,
  PlayerSnapshot,
  PossessionResult,
  PossessionSpec,
  RouteSpec,
  RunState,
  ScoreSnapshot,
  ShotGeometryDecision,
  ShotGeometryInput,
  ShotPreview,
  Vec2,
} from "./types.js";
import {
  CHECK_BALL_TICKS,
  GATHER_SET_TICKS,
  SHOT_CLOCK_TICKS,
  TICK_RATE,
} from "./types.js";

const RESOLVE_TICKS = 30;
const CONTEST_RADIUS = 0.034;
const RELEASE_POINT_LIFT = 0.026;
const HELPER_HOLD_TICKS = 72;

function routePosition(route: RouteSpec, elapsedTicks: number): Vec2 {
  if (route.kind === "hold") return { ...route.origin };
  const raw = ((elapsedTicks + route.phaseTicks) % route.periodTicks) / route.periodTicks;
  const cycle = raw < 0.5 ? raw * 2 : 2 - raw * 2;
  const progress = smoothstep(cycle);
  const base = lerp(route.origin, route.target, progress);
  const direction = normalize(subtract(route.target, route.origin));
  const perpendicular = { x: -direction.y, y: direction.x };
  const sway = Math.sin(raw * Math.PI * 2) * route.sway;
  return add(base, scale(perpendicular, sway));
}

function offenseAt(spec: PossessionSpec, elapsedTicks: number): PlayerSnapshot[] {
  return spec.offense.map((player) => {
    const position = routePosition(player.route, elapsedTicks);
    const next = routePosition(player.route, elapsedTicks + 1);
    return {
      id: player.id,
      position,
      velocity: subtract(next, position),
      hasBall: player.id === spec.ballhandler,
      gathering: false,
      gatherProgress: 0,
      callingForBall: false,
    };
  });
}

function offenseSpec(spec: PossessionSpec, id: PlayerId): OffensivePlayerSpec {
  const player = spec.offense.find((candidate) => candidate.id === id);
  if (!player) throw new Error(`Missing offensive player ${id}`);
  return player;
}

function player(state: RunState, id: PlayerId): PlayerSnapshot {
  const found = state.offense.find((candidate) => candidate.id === id);
  if (!found) throw new Error(`Missing offensive player ${id}`);
  return found;
}

function currentSpec(state: RunState): PossessionSpec {
  const spec = state.court.possessions[state.possessionIndex];
  if (!spec) throw new Error(`No possession ${state.possessionIndex}`);
  return spec;
}

function elapsedLiveTicks(state: RunState): number {
  return SHOT_CLOCK_TICKS - state.clockTicks;
}

function makeInitialDefense(spec: PossessionSpec): DefenderSnapshot[] {
  return spec.defense.map((defender) => ({
    id: defender.id,
    assignment: defender.assignment,
    position: { ...defender.start },
    velocity: { x: 0, y: 0 },
    hand: { ...defender.start },
    contesting: false,
    helpState: "home",
    helpingFrom: null,
  }));
}

function initialBall(spec: PossessionSpec, offense: readonly PlayerSnapshot[]): BallSnapshot {
  const owner = offense.find((candidate) => candidate.id === spec.ballhandler)!;
  return { position: { ...owner.position }, owner: spec.ballhandler, passingTo: null, passProgress: 0 };
}

function resetForPossession(state: RunState): void {
  const spec = currentSpec(state);
  state.phase = "check";
  state.phaseTicks = 0;
  state.clockTicks = SHOT_CLOCK_TICKS;
  state.ballhandler = spec.ballhandler;
  state.offense = offenseAt(spec, 0);
  state.defense = makeInitialDefense(spec);
  state.ball = initialBall(spec, state.offense);
  state.gather = null;
  state.pendingPass = null;
  state.transitionTicks = 0;
}

export function startRun(court: DailyCourt): RunState {
  if (court.possessions.length === 0) throw new Error("A run needs at least one possession");
  const first = court.possessions[0];
  const offense = offenseAt(first, 0);
  return {
    court,
    tick: 0,
    possessionIndex: 0,
    phase: "check",
    phaseTicks: 0,
    clockTicks: SHOT_CLOCK_TICKS,
    ballhandler: first.ballhandler,
    ball: initialBall(first, offense),
    offense,
    defense: makeInitialDefense(first),
    gather: null,
    pendingPass: null,
    passHistory: [],
    results: [],
    transitionTicks: 0,
  };
}

function updateOffense(state: RunState): void {
  const spec = currentSpec(state);
  const elapsed = elapsedLiveTicks(state);
  for (const snapshot of state.offense) {
    const route = offenseSpec(spec, snapshot.id).route;
    const nextPosition = state.gather?.shooter === snapshot.id
      ? state.gather.frozenAt
      : routePosition(route, elapsed);
    const futurePosition = state.gather?.shooter === snapshot.id
      ? state.gather.frozenAt
      : routePosition(route, elapsed + 1);
    snapshot.velocity = subtract(futurePosition, nextPosition);
    snapshot.position = { ...nextPosition };
    snapshot.hasBall = state.ball.owner === snapshot.id;
    snapshot.gathering = state.gather?.shooter === snapshot.id;
    snapshot.gatherProgress = snapshot.gathering
      ? clamp((state.tick - state.gather!.startedTick) / GATHER_SET_TICKS, 0, 1)
      : 0;
  }
}

function defensiveTarget(state: RunState, defenderId: DefenderId): Vec2 {
  const spec = currentSpec(state);
  const defenderSpec = spec.defense.find((candidate) => candidate.id === defenderId)!;
  const assigned = player(state, defenderSpec.assignment);
  const v2Defense = defenderSpec.accelerationPerTick !== undefined
    || defenderSpec.helpCommitment !== undefined
    || defenderSpec.stuntTicks !== undefined;

  if (!v2Defense) {
    let closingOn: PlayerId | null = state.ball.owner;
    if (state.pendingPass) {
      const sincePass = state.tick - state.pendingPass.startedTick;
      if (defenderSpec.assignment === state.pendingPass.to && sincePass < defenderSpec.reactionTicks) {
        closingOn = state.pendingPass.from;
      } else if (sincePass >= defenderSpec.reactionTicks) {
        closingOn = state.pendingPass.to;
      }
    }
    if (closingOn === defenderSpec.assignment) {
      const ballPlayer = player(state, defenderSpec.assignment);
      return add(ballPlayer.position, scale(normalize(subtract(HOOP, ballPlayer.position)), 0.075));
    }
    const help = lerp(assigned.position, HOOP, 0.38);
    return lerp(help, state.ball.position, 0.12);
  }

  const latestPass = [...state.passHistory]
    .reverse()
    .find((entry) => entry.possessionIndex === state.possessionIndex);

  if (latestPass?.to === defenderSpec.assignment) {
    const sincePass = state.tick - latestPass.launchedTick;
    if (sincePass < defenderSpec.reactionTicks) {
      const heldHelp = lerp(assigned.position, HOOP, 0.42);
      return lerp(heldHelp, state.ball.position, 0.08);
    }
  }

  if (latestPass?.helperId === defenderId && (defenderSpec.stuntTicks ?? 0) > 0) {
    const caughtTick = latestPass.caughtTick >= 0 ? latestPass.caughtTick : state.tick;
    const holdingReceiver = state.ball.owner === latestPass.to || state.pendingPass?.to === latestPass.to;
    const stuntEnds = caughtTick + Math.max(
      defenderSpec.stuntTicks ?? 0,
      holdingReceiver ? HELPER_HOLD_TICKS : 0,
    );
    if (state.tick <= stuntEnds) {
      const receiver = player(state, latestPass.to);
      const commitment = 0.72 + (defenderSpec.helpCommitment ?? 0) * 0.28;
      const help = lerp(assigned.position, HOOP, 0.38);
      const stunt = add(receiver.position, scale(normalize(subtract(HOOP, receiver.position)), 0.095));
      return lerp(help, stunt, commitment);
    }
  }

  if (state.ball.owner === defenderSpec.assignment) {
    const ballPlayer = player(state, defenderSpec.assignment);
    return add(ballPlayer.position, scale(normalize(subtract(HOOP, ballPlayer.position)), 0.075));
  }

  const help = lerp(assigned.position, HOOP, 0.38);
  const ballPosition = state.ball.position;
  return lerp(help, ballPosition, 0.12);
}

function updateHelpState(state: RunState, defender: DefenderSnapshot): void {
  const spec = currentSpec(state);
  const defenderSpec = spec.defense.find((candidate) => candidate.id === defender.id)!;
  const possessionPasses = state.passHistory
    .filter((entry) => entry.possessionIndex === state.possessionIndex);
  const currentPass = possessionPasses.at(-1);
  const latestPass = [...possessionPasses]
    .reverse()
    .find((entry) => entry.helperId === defender.id);
  if (!latestPass || (defenderSpec.stuntTicks ?? 0) === 0) {
    defender.helpState = "home";
    defender.helpingFrom = null;
    return;
  }
  const caughtTick = latestPass.caughtTick >= 0 ? latestPass.caughtTick : state.tick;
  const holdingReceiver = currentPass === latestPass
    && (state.ball.owner === latestPass.to || state.pendingPass?.to === latestPass.to);
  const stuntEnds = caughtTick + Math.max(
    defenderSpec.stuntTicks ?? 0,
    holdingReceiver ? HELPER_HOLD_TICKS : 0,
  );
  const recoveryEnds = stuntEnds + 30;
  defender.helpState = state.tick <= stuntEnds
    ? "stunting"
    : state.tick <= recoveryEnds
      ? "recovering"
      : "home";
  defender.helpingFrom = defender.helpState === "home" ? null : defenderSpec.assignment;
}

function updateDefense(state: RunState): void {
  const spec = currentSpec(state);
  for (const defender of state.defense) {
    const defenderSpec = spec.defense.find((candidate) => candidate.id === defender.id)!;
    const target = defensiveTarget(state, defender.id);
    const delta = subtract(target, defender.position);
    const desired = scale(normalize(delta), Math.min(defenderSpec.speedPerTick, distance(target, defender.position)));
    const acceleration = defenderSpec.accelerationPerTick;
    const movement = acceleration === undefined
      ? desired
      : (() => {
        const velocityChange = subtract(desired, defender.velocity);
        const accelerated = add(
          defender.velocity,
          scale(normalize(velocityChange), Math.min(acceleration, distance(velocityChange, { x: 0, y: 0 }))),
        );
        const capped = scale(
          normalize(accelerated),
          Math.min(defenderSpec.speedPerTick, distance(accelerated, { x: 0, y: 0 })),
        );
        return distance(target, defender.position) < distance(capped, { x: 0, y: 0 })
          ? delta
          : capped;
      })();
    defender.velocity = movement;
    defender.position = add(defender.position, movement);
    updateHelpState(state, defender);

    const shooter = state.ball.owner ? player(state, state.ball.owner) : null;
    const handTarget = shooter && defender.helpState === "stunting"
      ? releasePoint(shooter.position)
      : shooter?.position;
    const towardBall = handTarget ? normalize(subtract(handTarget, defender.position)) : { x: 0, y: 0 };
    const proximity = shooter ? distance(shooter.position, defender.position) : 1;
    const closing = shooter
      ? Math.max(0, defender.velocity.x * towardBall.x + defender.velocity.y * towardBall.y)
      : 0;
    const raise = Math.max(
      clamp((0.28 - proximity) / 0.18 + closing * 35, 0.15, 1),
      defender.helpState === "stunting" ? 0.9 : 0,
    );
    const reach = defenderSpec.armLength * (defender.helpState === "stunting" ? 1.9 : 1);
    defender.hand = add(defender.position, scale(towardBall, reach * raise));
    defender.contesting = proximity < 0.2 && raise > 0.55;
  }
}

function updatePass(state: RunState): void {
  const pass = state.pendingPass;
  if (!pass) return;
  const progress = clamp((state.tick - pass.startedTick) / pass.durationTicks, 0, 1);
  const receiver = player(state, pass.to);
  pass.end = { ...receiver.position };
  const flat = lerp(pass.start, pass.end, progress);
  state.ball.position = { x: flat.x, y: flat.y - Math.sin(progress * Math.PI) * 0.035 };
  state.ball.passProgress = progress;
  if (progress < 1) return;

  state.ballhandler = pass.to;
  state.ball.owner = pass.to;
  state.ball.passingTo = null;
  state.ball.passProgress = 0;
  state.ball.position = { ...receiver.position };
  receiver.hasBall = true;
  const record = state.passHistory[pass.historyIndex];
  record.caughtTick = state.tick;
  state.pendingPass = null;
}

function updateOwnedBall(state: RunState): void {
  if (state.pendingPass) return;
  const owner = player(state, state.ballhandler);
  state.ball.position = { ...owner.position };
  state.ball.owner = state.ballhandler;
}

function releasePoint(position: Vec2): Vec2 {
  return add(position, scale(normalize(subtract(HOOP, position)), RELEASE_POINT_LIFT));
}

export function adjudicateShotGeometry(input: ShotGeometryInput): ShotGeometryDecision {
  const start = releasePoint(input.shooterPosition);
  const corridorDistance = distanceToSegment(input.defenderHand, start, HOOP);
  const handPastRelease = distance(input.defenderHand, HOOP) < distance(start, HOOP) + 0.015;
  const set = input.gatherTicks >= GATHER_SET_TICKS;
  const obstructed = corridorDistance <= CONTEST_RADIUS && handPastRelease;
  const made = set && !obstructed;
  return {
    value: shotValue(input.shooterPosition),
    set,
    corridorDistance,
    obstructed,
    made,
    result: made ? "make" : set ? "altered" : "rushed",
  };
}

export function getShotPreview(state: RunState): ShotPreview | null {
  if (!state.gather || state.phase !== "live") return null;
  const shooter = player(state, state.gather.shooter);
  const candidates = state.defense.map((defender) => {
    const defenderDistance = distance(defender.position, shooter.position);
    const towardShooter = normalize(subtract(shooter.position, defender.position));
    const closingVelocity = defender.velocity.x * towardShooter.x + defender.velocity.y * towardShooter.y;
    const baseGeometry = adjudicateShotGeometry({
      gatherTicks: state.tick - state.gather!.startedTick,
      shooterPosition: shooter.position,
      defenderHand: defender.hand,
    });
    const committedStunt = currentSpec(state).secondPassFocus
      && defender.helpState === "stunting"
      && defenderDistance < 0.24
      && closingVelocity > 0.00035;
    const geometry = committedStunt && baseGeometry.set
      ? { ...baseGeometry, obstructed: true, made: false, result: "altered" as const }
      : baseGeometry;
    return { defender, defenderDistance, geometry };
  });
  const v2Defense = currentSpec(state).defense.some((defender) => defender.helpCommitment !== undefined);
  const obstructing = candidates
    .filter((candidate) => candidate.geometry.obstructed)
    .sort((left, right) => left.geometry.corridorDistance - right.geometry.corridorDistance)[0];
  const selected = v2Defense && obstructing
    ? obstructing
    : [...candidates].sort((left, right) => left.defenderDistance - right.defenderDistance)[0];
  const nearest = selected.defender;
  const nearestDistance = selected.defenderDistance;
  const geometry = selected.geometry;
  return {
    value: geometry.value,
    gatherTicks: state.tick - state.gather.startedTick,
    set: geometry.set,
    nearestDefenderId: nearest.id,
    defenderDistance: nearestDistance,
    hand: { ...nearest.hand },
    corridorDistance: geometry.corridorDistance,
    obstructed: geometry.obstructed,
  };
}

function scoreSnapshot(results: readonly PossessionResult[]): ScoreSnapshot {
  const points = results.reduce((sum, result) => sum + result.points, 0);
  const timeLeftTicks = results.reduce(
    (sum, result) => sum + (result.made ? result.clockTicksAtRelease : 0),
    0,
  );
  const tier = points >= 23
    ? "Automatic"
    : points >= 19
      ? "Closer"
      : points >= 14
        ? "Bucket Getter"
        : points >= 8
          ? "In Rhythm"
          : "Finding Rhythm";
  return { points, timeLeftTicks, timeLeftSeconds: timeLeftTicks / TICK_RATE, tier };
}

function resolve(state: RunState, result: PossessionResult): void {
  state.results.push(result);
  state.gather = null;
  state.pendingPass = null;
  state.ball.owner = null;
  state.ball.passingTo = null;
  state.phase = "resolved";
  state.phaseTicks = 0;
  state.transitionTicks = RESOLVE_TICKS;
}

function expire(state: RunState): void {
  const passes = state.passHistory.filter((pass) => pass.possessionIndex === state.possessionIndex).length;
  resolve(state, {
    possessionIndex: state.possessionIndex,
    shooter: state.ballhandler,
    value: null,
    result: "expired",
    made: false,
    points: 0,
    releaseTick: null,
    clockTicksAtRelease: 0,
    gatherTicks: state.gather ? state.tick - state.gather.startedTick : 0,
    defenderId: null,
    defenderDistance: null,
    handCorridorDistance: null,
    alteredBy: null,
    passes,
  });
}

function stepOne(state: RunState): void {
  if (state.phase === "complete") return;
  state.tick += 1;
  state.phaseTicks += 1;

  if (state.phase === "check") {
    if (state.phaseTicks >= CHECK_BALL_TICKS) {
      state.phase = "live";
      state.phaseTicks = 0;
    }
    return;
  }

  if (state.phase === "resolved") {
    state.transitionTicks -= 1;
    if (state.transitionTicks <= 0) {
      if (state.possessionIndex + 1 >= state.court.possessions.length) {
        state.phase = "complete";
        state.phaseTicks = 0;
      } else {
        state.possessionIndex += 1;
        resetForPossession(state);
      }
    }
    return;
  }

  state.clockTicks -= 1;
  updateOffense(state);
  updatePass(state);
  updateOwnedBall(state);
  updateDefense(state);
  if (state.clockTicks <= 0) {
    state.clockTicks = 0;
    expire(state);
  }
}

/** Advances an integer number of fixed 1/60-second simulation ticks. */
export function stepSimulation(state: RunState, ticks = 1): GameSnapshot {
  if (!Number.isInteger(ticks) || ticks < 0) throw new Error("ticks must be a non-negative integer");
  for (let index = 0; index < ticks; index += 1) stepOne(state);
  return getSnapshot(state);
}

export function pass(state: RunState, target: PlayerId): boolean {
  if (state.phase !== "live" || state.pendingPass || state.gather || target === state.ballhandler) return false;
  const receiver = state.offense.find((candidate) => candidate.id === target);
  if (!receiver) return false;
  const from = state.ballhandler;
  const start = { ...player(state, from).position };
  const end = { ...receiver.position };
  const durationTicks = clamp(Math.round(distance(start, end) * 34 + 12), 18, 30);
  const spec = currentSpec(state);
  const passMidpoint = lerp(start, end, 0.55);
  const onBallHelper = state.defense.find((candidate) => {
    const defenderSpec = spec.defense.find((entry) => entry.id === candidate.id)!;
    return defenderSpec.assignment === from && (defenderSpec.helpCommitment ?? 0) > 0;
  })?.id;
  const helper = onBallHelper ?? state.defense
    .filter((candidate) => {
      const defenderSpec = spec.defense.find((entry) => entry.id === candidate.id)!;
      return defenderSpec.assignment !== target && (defenderSpec.helpCommitment ?? 0) > 0;
    })
    .map((candidate) => {
      const defenderSpec = spec.defense.find((entry) => entry.id === candidate.id)!;
      const laneDistance = distance(candidate.position, passMidpoint);
      const receiverDistance = distance(candidate.position, end);
      return { id: candidate.id, cost: laneDistance * 0.15 + receiverDistance * 0.85 - (defenderSpec.helpCommitment ?? 0) * 0.08 };
    })
    .sort((a, b) => a.cost - b.cost)[0]?.id ?? null;
  const record: PassRecord = {
    possessionIndex: state.possessionIndex,
    from,
    to: target,
    launchedTick: state.tick,
    caughtTick: -1,
    clockTicksAtLaunch: state.clockTicks,
    helperId: helper ?? undefined,
  };
  state.passHistory.push(record);
  state.pendingPass = {
    from,
    to: target,
    startedTick: state.tick,
    durationTicks,
    start,
    end,
    historyIndex: state.passHistory.length - 1,
  };
  state.ball.owner = null;
  state.ball.passingTo = target;
  state.ball.passProgress = 0;
  return true;
}

export function gather(state: RunState): boolean {
  if (state.phase !== "live" || state.pendingPass || state.gather || state.ball.owner !== state.ballhandler) {
    return false;
  }
  const shooter = player(state, state.ballhandler);
  state.gather = { shooter: state.ballhandler, startedTick: state.tick, frozenAt: { ...shooter.position } };
  shooter.gathering = true;
  shooter.gatherProgress = 0;
  return true;
}

export function cancelGather(state: RunState): boolean {
  if (!state.gather || state.phase !== "live") return false;
  state.gather = null;
  for (const playerSnapshot of state.offense) {
    playerSnapshot.gathering = false;
    playerSnapshot.gatherProgress = 0;
  }
  return true;
}

export function release(state: RunState): PossessionResult | null {
  if (!state.gather || state.phase !== "live") return null;
  const preview = getShotPreview(state)!;
  const gatherTicks = state.tick - state.gather.startedTick;
  const made = preview.set && !preview.obstructed;
  const result = made ? "make" : preview.set ? "altered" : "rushed";
  const contestingDefender = state.defense.find((defender) => defender.id === preview.nearestDefenderId);
  const alteredBy = result === "altered"
    ? contestingDefender && contestingDefender.helpState !== "home" ? "help" : "closeout"
    : null;
  const passes = state.passHistory.filter((entry) => entry.possessionIndex === state.possessionIndex).length;
  const adjudication: PossessionResult = {
    possessionIndex: state.possessionIndex,
    shooter: state.gather.shooter,
    value: preview.value,
    result,
    made,
    points: made ? preview.value : 0,
    releaseTick: state.tick,
    clockTicksAtRelease: state.clockTicks,
    gatherTicks,
    defenderId: preview.nearestDefenderId,
    defenderDistance: preview.defenderDistance,
    handCorridorDistance: preview.corridorDistance,
    alteredBy,
    passes,
  };
  resolve(state, adjudication);
  return adjudication;
}

export function getSnapshot(state: RunState): GameSnapshot {
  const score = scoreSnapshot(state.results);
  return {
    phase: state.phase,
    tick: state.tick,
    possessionIndex: state.possessionIndex,
    possessionCount: state.court.possessions.length,
    clockTicks: state.clockTicks,
    clockSeconds: state.clockTicks / TICK_RATE,
    ballhandler: state.ballhandler,
    offense: state.offense.map((entry) => ({
      ...entry,
      position: { ...entry.position },
      velocity: { ...entry.velocity },
      callingForBall: state.defense.some((defender) => (
        defender.helpingFrom === entry.id && defender.helpState !== "home" && entry.id !== state.ballhandler
      )),
    })),
    defense: state.defense.map((entry) => ({
      ...entry,
      position: { ...entry.position },
      velocity: { ...entry.velocity },
      hand: { ...entry.hand },
    })),
    ball: { ...state.ball, position: { ...state.ball.position } },
    shotPreview: getShotPreview(state),
    score,
    passHistory: state.passHistory.map((entry) => ({ ...entry })),
    results: state.results.map((entry) => ({ ...entry })),
    lastResult: state.results.length > 0 ? { ...state.results[state.results.length - 1] } : null,
  };
}

export function compareScores(a: ScoreSnapshot, b: ScoreSnapshot): number {
  return a.points !== b.points ? a.points - b.points : a.timeLeftTicks - b.timeLeftTicks;
}

export function scoreRun(state: RunState): ScoreSnapshot {
  return scoreSnapshot(state.results);
}
